import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { extractApiError, mensajeDelServidor } from '@utils/apiError';

const TOKEN_KEY = 'erp_token';
const REFRESH_IN_PROGRESS_KEY = 'erp_refresh_in_progress';

// ─── Axios Instance ───────────────────────────────────────────
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // Para enviar la httpOnly cookie del refresh token
});

// ─── Request Interceptor ──────────────────────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response Interceptor ─────────────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });
  failedQueue = [];
}

function handleLogout(): void {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_IN_PROGRESS_KEY);
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // ── 401: intentar refresh del token ───────────────────────
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      if (isRefreshing) {
        // Encolar la request mientras se refresca
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await apiClient.post<{ data: { access_token: string } }>(
          '/auth/refresh',
          {},
        );
        const { access_token } = response.data.data;
        localStorage.setItem(TOKEN_KEY, access_token);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        processQueue(null, access_token);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ── 5xx: toast de error de servidor ───────────────────────
    const status = error.response?.status;

    // ── 400/403/409/422/423: rechazos de negocio → mostrar mensaje ───────
    // 409 DUPLICADO / ESTADO_INVALIDO (incl. FK/DUP mapeados desde MySQL, BUG-101)
    //
    // 🔇 I-RESUMEN, Tarea 4 — «se omite, sin decir que se omitió» (regla de Mau para
    // información que no te toca ver, la contraria a la de acciones). Un bloque de la casa
    // del proyecto que consulta un dato con candado de ROL (no de nivel) — como el dinero
    // vía `verificarModulo('egresos')` — puede recibir un 403 LEGÍTIMO para un vendedor sin
    // que eso sea un error: es la pantalla completa de esa persona. Sin este escape, el
    // interceptor global disparaba el toast igual, delatando "aquí había algo y no te lo
    // pude traer" — la fuga que la regla de Mau prohíbe, aunque el componente ya omitiera
    // el bloque en silencio. El escape es por-request (header), nunca global: un 403
    // inesperado en cualquier otra pantalla sigue avisando igual que siempre.
    const omitirPorCandadoDeRol = originalRequest.headers?.['X-Omitir-Toast-Candado-Rol'] === '1';
    if (status === 403 && omitirPorCandadoDeRol) {
      return Promise.reject(error);
    }
    // 🔇 I-257 (F5 del mapa) — el PRIMER uso del Overhead (todavía sin proyecto corporativo ni cuenta del fondo) no es un error: es un estado
    // vacío que la propia pantalla ya explica («Falta configurar…»). El toast rojo lo hacía parecer una falla. Sólo LECTURAS (GET) y sólo
    // estos dos códigos: una escritura que rebota por lo mismo SÍ avisa, y cualquier otro 422 sigue avisando igual que siempre.
    const codigoEstadoVacio = (error.response?.data as { code?: string })?.code;
    const esEstadoVacioDelOverhead = status === 422
      && originalRequest.method?.toLowerCase() === 'get'
      && (codigoEstadoVacio === 'PROYECTO_CORPORATIVO_NO_CONFIGURADO' || codigoEstadoVacio === 'CUENTA_FONDO_NO_CONFIGURADA');
    if (esEstadoVacioDelOverhead) {
      return Promise.reject(error);
    }
    // 🔇 I-257 — «pago parecido» no es un error: la ficha de captura pinta el aviso y pide la nota (D-2026-10-01-G.5).
    if (status === 409 && codigoEstadoVacio === 'PAGO_PARECIDO') {
      return Promise.reject(error);
    }
    // 🔇 E-4 (validación 5-oct) — «el aviso lo da quien llamó». Quien manda esta cabecera ya pinta el mensaje BUENO del servidor
    // junto a su botón; el aviso genérico de aquí (5xx / 503) salía ENCIMA como dos toasts sin causa. Por-request, nunca global.
    if (originalRequest.headers?.['X-Aviso-Propio'] === '1') {
      return Promise.reject(error);
    }
    if (status && [400, 403, 409, 422, 423].includes(status)) {
      const msg = extractApiError(error, 'No se pudo completar la acción.');
      toast.error(msg, { id: msg });
    }

    // ── 502 POLIZA_FALLIDA: la operación SÍ se ejecutó ──────────────────
    // M01 responde 502 cuando el ingreso quedó autorizado/conciliado y lo único que
    // falló fue la póliza contable (`ingresos.routes.ts`). Tratarlo como «error del
    // servidor» hacía dos daños: escondía el descuadre contable y le pedía al operador
    // que reintentara una operación que ya se hizo (el reintento rebota con 409).
    const codigoNegocio = (error.response?.data as { code?: string })?.code;
    if (status && status >= 500 && codigoNegocio === 'POLIZA_FALLIDA') {
      // BUG-4122: NO va por extractApiError — ante un 5xx sin texto diría «intenta de nuevo», y esto ya se hizo.
      const msg = mensajeDelServidor(error) ?? 'La operación se registró, pero la póliza contable no pudo generarse — avísale a Contabilidad.';
      toast.error(msg, { id: msg });
    } else if (status === 503) {
      // BUG-4292: un 503 dice POR QUÉ no salió (el guardián: «no se guardó, vuelve a intentarlo»), en un solo aviso. Sin texto, el del dueño (T14.14f).
      const msg = extractApiError(error);
      toast.error(msg, { id: msg });
    } else if (status && status >= 500) {
      toast.error('Error del servidor. Por favor intenta de nuevo.', { id: 'Error del servidor. Por favor intenta de nuevo.' });
    }

    // ── 429: rate limit ────────────────────────────────────────
    if (status === 429) {
      toast.error('Demasiadas solicitudes. Espera un momento.', { id: 'Demasiadas solicitudes. Espera un momento.' });
    }

    return Promise.reject(error);
  },
);

export default apiClient;
export { apiClient };

// I-228: `traerTodasLasPaginas` (I-204) se apagó — su lugar es `utils/todasLasPaginas.ts` `traerTodas` (un solo motor).

// ─── Auth API ─────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post<{ data: { access_token: string; token_type: string; expires_in: number; usuario: import('@/types').Usuario } }>(
      '/auth/login',
      // El inquilino sale de la configuración, no de un literal. El backend ya lo trata
      // como opcional con respaldo (`auth.routes.ts:94` → `DEFAULT_TENANT_SLUG`), y el
      // acceso del portal broker ya lo lee así (`LoginPortalBroker.tsx:46`): era el único
      // login que lo llevaba clavado, y por eso NINGUNA prueba de punta a punta podía
      // correr contra una base que no fuera la de desarrollo. Sin la variable, el valor
      // es el de siempre: producción no cambia.
      // `?.trim() ||` y no `??`: una variable VACÍA con `??` pasaría tal cual y el login
      // pediría un inquilino sin nombre — 403 para todo el mundo. Mismo patrón que
      // `testTenantSlug()` en el backend. Lo cazó la prueba, no el razonamiento.
      { email, password, tenant: import.meta.env.VITE_TENANT_SLUG?.trim() || 'rm-desarrollos' },
    ),

  logout: () =>
    apiClient.post('/auth/logout'),

  refresh: () =>
    apiClient.post<{ data: { access_token: string; token_type: string; expires_in: number } }>('/auth/refresh'),

  // BUG-629: canjea el enlace de un solo uso que ahora llega por correo en vez
  // de la contraseña en texto plano.
  establecerPassword: (token: string, passwordNueva: string) =>
    apiClient.post<{ message: string }>('/auth/establecer-password', { token, password_nueva: passwordNueva }),

  me: () =>
    apiClient.get<{ data: import('@/types').Usuario }>('/auth/me'),
};

export { TOKEN_KEY };
